import { Button, Dropdown } from 'antd'
import React, { useRef, useCallback } from 'react'
import { BsThreeDots } from "react-icons/bs";
import { IoMdDownload } from "react-icons/io";
import { MdFileUpload } from "react-icons/md";
import type { MenuProps } from 'antd';
import { getBCCKeywordsCSVTemplate } from '@/ts/retrieve-data';
import { IBCCKeyword, BCC_KEYWORD_TYPES, KWD_BID_TYPES, KeywordTypeValues, BCC_CSV_UPLOAD_COLUMNS } from '@/ts/common-types';
import * as XLSX from 'xlsx';
import { showToast } from '@/ts/modals';

interface ICsvUploadButtonProps {
  keywords: IBCCKeyword[];
  selectedKeywords: IBCCKeyword[];
  modalParams: any;
  handleKeywordSelection: (selectedRows: any[]) => void;
  handleFieldChange: (clusterId: string, collectionId: string, field: string, value: any, record?: any) => void;
  handleBatchKeywordFieldUpdates?: (updates: Array<{ clusterId: string; collectionId: string; field: string; value: any; record: any }>) => void;
  handleAddCustomKeyword: (keyword: IBCCKeyword) => void;
  tableConfig?: any;
}

interface ParsedKeywordRow {
  keyword: string;
  type: string;
  bid: number | null;
}

const CsvUploadButton: React.FC<ICsvUploadButtonProps> = ({
  keywords,
  selectedKeywords,
  modalParams,
  handleKeywordSelection,
  handleFieldChange,
  handleBatchKeywordFieldUpdates,
  handleAddCustomKeyword,
  tableConfig,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseFile = useCallback(async (file: File): Promise<ParsedKeywordRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { 
            raw: false,
            defval: ''
          });
          
          // Find the column names (case-insensitive)
          const rows: ParsedKeywordRow[] = [];
          
          jsonData.forEach((row) => {
            // Find keyword, type, and bid columns (case-insensitive)
            let keyword = '';
            let type = '';
            let bid: number | null = null;
            
            Object.keys(row).forEach((key) => {
              const lowerKey = key.toLowerCase().trim();
              const value = row[key];
              
              if (lowerKey === BCC_CSV_UPLOAD_COLUMNS.KEYWORD) {
                keyword = String(value || '').trim();
              } else if (lowerKey === BCC_CSV_UPLOAD_COLUMNS.TYPE) {
                type = String(value || '').trim();
              } else if (lowerKey === BCC_CSV_UPLOAD_COLUMNS.BID) {
                const bidValue = parseFloat(String(value));
                bid = !isNaN(bidValue) ? bidValue : null;
              }
            });
            
            // Only add if keyword is present
            if (keyword) {
              rows.push({ keyword, type, bid });
            }
          });
          
          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const processUploadedKeywords = useCallback(async (parsedRows: ParsedKeywordRow[]) => {
    const { clusterId, collectionId } = modalParams || {};
    if (!clusterId || !collectionId) {
      showToast('danger', 'Invalid modal parameters');
      return;
    }

    // Track keywords to select and bids to update
    const keywordsToSelect: IBCCKeyword[] = [...selectedKeywords];
    const bidUpdates: { keyword: IBCCKeyword; bid: number }[] = [];
    const customKeywordsToAdd: { keyword: IBCCKeyword; bid: number | null }[] = [];
    
    let matchedCount = 0;
    let customAddedCount = 0;
    let skippedCount = 0;

    parsedRows.forEach((row) => {
      // Skip if keyword name is missing
      if (!row.keyword) {
        skippedCount++;
        return;
      }

      // Find matching keyword in the suggested keywords list (case-insensitive)
      const matchingKeyword = keywords.find((kw) => 
        kw.keyword?.toLowerCase() === row.keyword.toLowerCase()
      );

      if (matchingKeyword) {
        // Keyword exists in our list
        // Check if it's already selected
        const isAlreadySelected = keywordsToSelect.some((kw) => 
          kw.keyword?.toLowerCase() === row.keyword.toLowerCase() &&
          kw.keyword_type === matchingKeyword.keyword_type
        );

        if (!isAlreadySelected) {
          // Add to selection (use the keyword type we have, not the sheet's type)
          keywordsToSelect.push({
            ...matchingKeyword,
            clusterId,
            id: collectionId,
            key: `${clusterId}||${collectionId}||${matchingKeyword.keyword}||${matchingKeyword.keyword_type}`,
          });
        }

        // Update bid if provided in sheet
        if (row.bid !== null && row.bid > 0) {
          bidUpdates.push({
            keyword: {
              ...matchingKeyword,
              clusterId,
              id: collectionId,
              key: `${clusterId}||${collectionId}||${matchingKeyword.keyword}||${matchingKeyword.keyword_type}`,
            },
            bid: row.bid,
          });
        }

        matchedCount++;
      } else {
        // Keyword doesn't exist in our list - add as custom keyword
        // Check if custom keyword already exists in the list or in customKeywordsToAdd
        const customExistsInList = keywords.some((kw) => 
          kw.keyword?.toLowerCase() === row.keyword.toLowerCase() && 
          (kw.keyword_type === KeywordTypeValues.CUSTOM || kw.isCustom)
        );
        
        const customExistsInPending = customKeywordsToAdd.some(({ keyword: kw }) =>
          kw.keyword?.toLowerCase() === row.keyword.toLowerCase()
        );

        if (!customExistsInList && !customExistsInPending) {
          // Create custom keyword object with null bid (bid will be set via overrides)
          const customKeyword: IBCCKeyword = {
            keyword: row.keyword,
            keyword_type: BCC_KEYWORD_TYPES.CUSTOM,
            clusterId,
            id: collectionId,
            key: `${clusterId}||${collectionId}||${row.keyword}||${KeywordTypeValues.CUSTOM}`,
            isCustom: true,
            exact_match_bid: null,  // Set to null, bid will be stored in overrides
            smart_match_bid: null,
            bid_booster: null,
          };

          // Add default null values for other columns from tableConfig
          tableConfig?.colDefs?.forEach((col: any) => {
            if (!col.dataIndex) return;
            const dataIndex = col.dataIndex;
            if (!(dataIndex in customKeyword)) {
              if (dataIndex.includes('_change')) {
                (customKeyword as any)[dataIndex] = 0;
              } else if (dataIndex !== 'keyword' && dataIndex !== 'keyword_type') {
                (customKeyword as any)[dataIndex] = null;
              }
            }
          });

          // Store bid from CSV to be applied via overrides later
          customKeywordsToAdd.push({ keyword: customKeyword, bid: row.bid });
          customAddedCount++;
        }
      }
    });

    // Add custom keywords first
    customKeywordsToAdd.forEach(({ keyword: customKw }) => {
      handleAddCustomKeyword(customKw);
    });

    // Wait a bit for custom keywords to be added to the list
    if (customKeywordsToAdd.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Update keyword selection (filter out negative keywords to avoid issues)
    const regularKeywordsToSelect = keywordsToSelect.filter(
      (kw) => kw.keyword_type !== BCC_KEYWORD_TYPES.NEGATIVE
    );
    
    // Also add custom keywords to selection
    const allToSelect = [...regularKeywordsToSelect];
    customKeywordsToAdd.forEach(({ keyword: customKw }) => {
      if (!allToSelect.some(kw => kw.key === customKw.key)) {
        allToSelect.push(customKw);
      }
    });

    handleKeywordSelection(allToSelect);

    // Update bids after selection - batch all updates together
    setTimeout(() => {
      // Collect all bid updates to apply in a single batched call
      const allBidUpdates: Array<{ clusterId: string; collectionId: string; field: string; value: any; record: any }> = [];
      
      // Add regular keyword bid updates
      bidUpdates.forEach(({ keyword, bid }) => {
        allBidUpdates.push({
          clusterId,
          collectionId,
          field: KWD_BID_TYPES.EXACT_MATCH_BID,
          value: bid,
          record: keyword
        });
      });

      // Also add custom keyword bid updates
      customKeywordsToAdd.forEach(({ keyword: customKw, bid }) => {
        if (bid !== null && bid !== undefined && bid > 0) {
          allBidUpdates.push({
            clusterId,
            collectionId,
            field: KWD_BID_TYPES.EXACT_MATCH_BID,
            value: bid,
            record: customKw
          });
        }
      });

      // Apply all updates in a single batched call if available
      if (allBidUpdates.length > 0 && handleBatchKeywordFieldUpdates) {
        handleBatchKeywordFieldUpdates(allBidUpdates);
      } else if (allBidUpdates.length > 0) {
        // Fallback to individual updates if batched function not available
        allBidUpdates.forEach(update => {
          handleFieldChange(
            update.clusterId,
            update.collectionId,
            update.field,
            update.value,
            update.record
          );
        });
      }
    }, 200);

    // Show summary message
    const messages = [];
    if (matchedCount > 0) messages.push(`${matchedCount} keywords matched and selected`);
    if (customAddedCount > 0) messages.push(`${customAddedCount} custom keywords added`);
    if (skippedCount > 0) messages.push(`${skippedCount} rows skipped`);
    
    if (messages.length > 0) {
      showToast('success', messages.join(', '));
    } else {
      showToast('warning', 'No keywords were processed from the file');
    }
  }, [keywords, selectedKeywords, modalParams, handleKeywordSelection, handleFieldChange, handleBatchKeywordFieldUpdates, handleAddCustomKeyword, tableConfig]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
      'text/csv',
      'application/csv',
    ];
    
    const isValidType = validTypes.includes(file.type) || 
      file.name.endsWith('.xlsx') || 
      file.name.endsWith('.xls') || 
      file.name.endsWith('.csv');

    if (!isValidType) {
      showToast('danger', 'Please upload a valid Excel (.xlsx, .xls) or CSV file');
      return;
    }

    try {
      showToast('warning', 'Processing file...');
      const parsedRows = await parseFile(file);
      
      if (parsedRows.length === 0) {
        showToast('warning', 'No valid data found in file. Make sure the file has "keyword", "type", and "bid" columns.');
        return;
      }

      await processUploadedKeywords(parsedRows);
    } catch (error) {
      console.error('Error processing file:', error);
      showToast('danger', 'Failed to process file. Please check the file format.');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [parseFile, processUploadedKeywords]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const menuItems: MenuProps['items'] = [
    {
      key: 'download',
      label: 'Download Template',
      icon: <IoMdDownload />,
      onClick: () => {
        getBCCKeywordsCSVTemplate();
      },
    },
    {
      key: 'upload',
      label: 'Bulk Upload',
      icon: <MdFileUpload />,
      onClick: handleUploadClick,
    },
  ];

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
      />
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
        <Button className='ms-3' type="default" icon={<BsThreeDots />} />
      </Dropdown>
    </>
  )
}

export default CsvUploadButton
